import { rootApi } from '../../services/rootApi';
import { courseApi, sectionApi, pageApi, pageModuleApi, pageRowApi, quizApi } from '../../utils/urls';

/**
 * RTK Query API slice for Edit Course domain
 *
 * This API provides endpoints for the modern course editor, mapping all legacy
 * course editing functionality to RTK Query patterns.
 *
 * Cache Tags Strategy (Entity-Based):
 * - Course: Main course entity
 *   - { type: 'Course', id: courseId } - Specific course
 *   - { type: 'Course', id: 'LIST' } - List of courses (for dashboard invalidation)
 * - Section: Course sections/chapters
 *   - { type: 'Section', id: sectionId } - Specific section
 *   - { type: 'Section', id: 'LIST' } - List of sections (invalidated on course TOC changes)
 * - Page: Individual pages
 *   - { type: 'Page', id: pageId } - Specific page
 *   - { type: 'Page', id: 'LIST' } - List of pages (invalidated on section changes)
 * - Module: Page modules/content blocks
 *   - { type: 'Module', id: moduleId } - Specific module
 *   - { type: 'Module', id: 'LIST' } - List of modules (invalidated on page changes)
 * - Layout: Page layout configuration
 *   - { type: 'Layout', id: pageId } - Layout for specific page
 *
 * File Uploads:
 * - Use existing fileUploaderApi for all file uploads
 * - Import: useImageUploadMutation, useFileUploadMutation from '../../features/fileUploader/hooks/useFileUploaderActions'
 * - These handle FormData, progress tracking, and proper backend integration
 *
 * Legacy references:
 * - src/legacy/src/js/stores/Course.js
 * - src/legacy/src/js/stores/PageLayout.js
 * - src/legacy/src/js/actions/PageModule.js
 */
export const editCourseApiSlice = rootApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    
    // ==================== COURSE STRUCTURE MANAGEMENT ====================
    
    /**
     * Fetch complete course data for editor with TOC and editing metadata
     * @param {number} courseId - The course ID
     * @returns {Object} Course with full TOC, sections, pages, and edit permissions
     */
    getCourseForBuilder: builder.query({
      query: ({ courseId }) => ({
        url: courseApi(),
        method: 'POST',
        body: { method: 'findById', data: { id: courseId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (_result, _error, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Section', id: 'LIST' },
        { type: 'Page', id: 'LIST' },
      ],
    }),

    /**
     * Update course table of contents (reorder sections/pages)
     * @param {number} courseId - The course ID
     * @param {Array} toc - New TOC structure with sections and pages
     * @returns {Object} Updated course data
     */
    updateCourseToc: builder.mutation({
      query: ({ courseId, toc }) => ({
        url: courseApi(),
        method: 'POST',
        body: { method: 'updateToc', data: { id: courseId, toc } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Section', id: 'LIST' },
        { type: 'Page', id: 'LIST' },
      ],
    }),

    /**
     * Create new section in course
     * @param {number} courseId - The course ID
     * @param {string} name - Section name
     * @param {number} order - Section order position
     * @returns {Object} Created section data
     */
    createSection: builder.mutation({
      query: ({ courseId, name, order }) => ({
        url: sectionApi(),
        method: 'POST',
        body: { method: 'create', data: { courseId, name, order } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Section', id: 'LIST' },
      ],
    }),


    /**
     * Delete section from course
     * @param {number} sectionId - The section ID
     * @param {number} courseId - The course ID (for cache invalidation)
     * @returns {Object} Deletion confirmation
     */
    deleteSection: builder.mutation({
      query: ({ sectionId }) => ({
        url: sectionApi(),
        method: 'POST',
        body: { method: 'delete', data: { id: sectionId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId, sectionId }) => [
        { type: 'Course', id: courseId },
        { type: 'Section', id: sectionId },
        { type: 'Section', id: 'LIST' },
        { type: 'Page', id: 'LIST' },
      ],
    }),

    /**
     * Delete quiz section from course
     * Quiz sections require a different API endpoint that also cleans up quiz settings
     * @param {number} sectionId - The quiz section ID
     * @param {number} courseId - The course ID (for cache invalidation and quiz cleanup)
     * @returns {Object} Deletion confirmation
     */
    deleteQuizSection: builder.mutation({
      query: ({ sectionId, courseId }) => ({
        url: quizApi(),
        method: 'POST',
        body: { method: 'deleteQuizSection', data: { sectionId, courseId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId, sectionId }) => [
        { type: 'Course', id: courseId },
        { type: 'Section', id: sectionId },
        { type: 'Section', id: 'LIST' },
        { type: 'Page', id: 'LIST' },
      ],
    }),

    /**
     * Create new page in section
     * @param {number} sectionId - The section ID
     * @param {number} courseId - The course ID (for cache invalidation)
     * @param {string} name - Page name
     * @param {number} order - Page order position
     * @returns {Object} Created page data
     */
    createPage: builder.mutation({
      query: ({ sectionId, name, order }) => ({
        url: pageApi(),
        method: 'POST',
        body: { method: 'create', data: { sectionId, name, order } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Page', id: 'LIST' },
      ],
    }),

    /**
     * Update page metadata (name, settings, visibility)
     * @param {number} pageId - The page ID
     * @param {number} courseId - The course ID (for cache invalidation)
     * @param {Object} data - Page data to update (name, order, settings, etc.)
     * @returns {Object} Updated page data
     */
    updatePage: builder.mutation({
      query: ({ pageId, courseId, ...data }) => ({
        url: pageApi(),
        method: 'POST',
        body: { method: 'update', data: { id: pageId, ...data } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId, pageId }) => [
        { type: 'Course', id: courseId },
        { type: 'Page', id: pageId },
        { type: 'Page', id: 'LIST' },
      ],
    }),

    /**
     * Delete page from course
     * @param {number} pageId - The page ID
     * @param {number} courseId - The course ID (for cache invalidation)
     * @returns {Object} Deletion confirmation
     */
    deletePage: builder.mutation({
      query: ({ pageId }) => ({
        url: pageApi(),
        method: 'POST',
        body: { method: 'delete', data: { id: pageId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId, pageId }) => [
        { type: 'Course', id: courseId },
        { type: 'Page', id: pageId },
        { type: 'Page', id: 'LIST' },
        { type: 'Module', id: 'LIST' },
      ],
    }),

    /**
     * Move page to a different section
     * @param {number} pageId - The page ID to move
     * @param {number} sectionId - The target section ID
     * @param {number} position - 1-indexed position in the target section
     * @param {number} courseId - The course ID (for cache invalidation)
     * @returns {Object} Updated page data
     */
    movePageToSection: builder.mutation({
      query: ({ pageId, sectionId, position }) => ({
        url: pageApi(),
        method: 'POST',
        body: { method: 'moveToSection', data: { pageId, sectionId, position } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId, pageId }) => [
        { type: 'Course', id: courseId },
        { type: 'Page', id: pageId },
        { type: 'Page', id: 'LIST' },
        { type: 'Section', id: 'LIST' },
      ],
    }),

    /**
     * Reorder pages within a section
     * @param {number} sectionId - The section ID
     * @param {number} courseId - The course ID (for cache invalidation)
     * @param {Array<number>} pageIds - Array of page IDs in new order
     * @returns {Object} Updated section with reordered pages
     */
    reorderPages: builder.mutation({
      query: ({ sectionId, pageIds }) => ({
        url: sectionApi(),
        method: 'POST',
        body: { method: 'reorderPages', data: { id: sectionId, pageIds } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Page', id: 'LIST' },
      ],
    }),

    /**
     * Reorder sections within a course
     * @param {number} courseId - The course ID
     * @param {Array<number>} sectionIds - Array of section IDs in new order
     * @returns {Object} Updated course with reordered sections
     */
    reorderSections: builder.mutation({
      query: ({ courseId, sectionIds }) => ({
        url: courseApi(),
        method: 'POST',
        body: { method: 'reorderSections', data: { id: courseId, sectionIds } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Section', id: 'LIST' },
      ],
    }),

    /**
     * Rename a section (update name only)
     * @param {number} sectionId - The section ID
     * @param {number} courseId - The course ID (for cache invalidation)
     * @param {string} name - New section name
     * @returns {Object} Updated section data
     */
    renameSection: builder.mutation({
      query: ({ sectionId, name }) => ({
        url: sectionApi(),
        method: 'POST',
        body: { method: 'update', data: { id: sectionId, name } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId, sectionId }) => [
        { type: 'Course', id: courseId },
        { type: 'Section', id: sectionId },
        { type: 'Section', id: 'LIST' },
      ],
    }),

    /**
     * Duplicate section (creates copy with all pages)
     * @param {number} sectionId - The section ID to duplicate
     * @param {number} courseId - The course ID (for cache invalidation)
     * @returns {Object} Created duplicate section data
     */
    duplicateSection: builder.mutation({
      query: ({ sectionId }) => ({
        url: sectionApi(),
        method: 'POST',
        body: { method: 'duplicate', data: { id: sectionId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Section', id: 'LIST' },
        { type: 'Page', id: 'LIST' },
      ],
    }),

    /**
     * Duplicate page (creates copy in same section)
     * @param {number} pageId - The page ID to duplicate
     * @param {number} courseId - The course ID (for cache invalidation)
     * @returns {Object} Created duplicate page data
     */
    duplicatePage: builder.mutation({
      query: ({ pageId }) => ({
        url: pageApi(),
        method: 'POST',
        body: { method: 'duplicate', data: { id: pageId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: 'Course', id: courseId },
        { type: 'Page', id: 'LIST' },
      ],
    }),

    // ==================== PAGE MODULE MANAGEMENT ====================
    
    /**
     * Fetch all modules for a specific page
     * @param {number} pageId - The page ID
     * @returns {Object} Page data with pageRows containing modules
     */
    getPageModules: builder.query({
      query: ({ pageId }) => ({
        url: pageApi(),
        method: 'POST',
        body: { method: 'findById', data: { id: pageId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (_result, _error, { pageId }) => [
        { type: 'Page', id: pageId },
        { type: 'Module', id: 'LIST' },
      ],
    }),

    /**
     * Create new page module (text, image, video, task)
     * @param {string} type - Module type ('text', 'image', 'video', 'task')
     * @param {number} pageId - The page ID
     * @param {boolean} newRow - Whether to create module in new row
     * @param {string} content - Module content (optional)
     * @param {Object} params - Module parameters/settings (optional)
     * @returns {Object} Updated page with new module
     */
    createPageModule: builder.mutation({
      query: ({ type, pageId, newRow = true, content, params, sources, caption }) => {
        const data = { type, pageId, newRow };

        // Add optional fields if provided
        if (content !== undefined) data.content = content;
        if (params !== undefined) data.params = params;
        if (sources !== undefined) data.sources = sources;
        if (caption !== undefined) data.caption = caption;

        return {
          url: pageModuleApi(),
          method: 'POST',
          body: { method: 'create', data },
        };
      },
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { pageId }) => [
        { type: 'Page', id: pageId },
        { type: 'Module', id: 'LIST' },
      ],
    }),

    /**
     * Update page module content, settings, and layout position
     * @param {number} moduleId - The module ID
     * @param {number} pageId - The page ID (for cache invalidation)
     * @param {string} content - Module content (HTML for text, URL for media, etc.)
     * @param {Object} params - Module parameters/settings
     * @param {Array} sources - Media sources (for video modules)
     * @param {string} caption - Module caption
     * @param {number} pageRowId - The row ID (for moving module to different row)
     * @param {number} sortOrder - The sort order within the row (1-based index)
     * @returns {Object} Updated module data
     */
    updatePageModule: builder.mutation({
      query: ({ moduleId, pageId, content, params, sources, caption, pageRowId, sortOrder }) => {
        // Build data object with only defined properties
        const data = { id: moduleId };
        if (content !== undefined) data.content = content;
        if (params !== undefined) data.params = params;
        if (sources !== undefined) data.sources = sources;
        if (caption !== undefined) data.caption = caption;
        if (pageRowId !== undefined) data.pageRowId = pageRowId;
        if (sortOrder !== undefined) data.sortOrder = sortOrder;

        return {
          url: pageModuleApi(),
          method: 'POST',
          body: { method: 'update', data },
        };
      },
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { pageId, moduleId }) => [
        { type: 'Page', id: pageId },
        { type: 'Layout', id: pageId },
        { type: 'Module', id: moduleId },
        { type: 'Module', id: 'LIST' },
      ],
    }),

    /**
     * Delete page module
     * @param {number} moduleId - The module ID
     * @param {number} pageId - The page ID (for cache invalidation)
     * @returns {Object} Deletion confirmation
     */
    deletePageModule: builder.mutation({
      query: ({ moduleId }) => ({
        url: pageModuleApi(),
        method: 'POST',
        body: { method: 'delete', data: { id: moduleId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { pageId, moduleId }) => [
        { type: 'Page', id: pageId },
        { type: 'Module', id: moduleId },
        { type: 'Module', id: 'LIST' },
      ],
    }),

    /**
     * Duplicate existing page module
     * NOTE: This method is not documented in API docs but exists in the backend
     * Legacy code doesn't use this - instead it creates a new module with same content
     * @param {number} moduleId - The module ID to duplicate
     * @param {number} pageId - The page ID
     * @returns {Object} Created duplicate module data
     */
    duplicatePageModule: builder.mutation({
      query: ({ moduleId, pageId }) => ({
        url: pageModuleApi(),
        method: 'POST',
        body: { method: 'duplicate', data: { id: moduleId, pageId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { pageId }) => [
        { type: 'Page', id: pageId },
        { type: 'Module', id: 'LIST' },
      ],
    }),

    // ==================== PAGE LAYOUT MANAGEMENT ====================

    /**
     * Get page data including layout structure (rows and columns)
     * Uses the same findById method as viewCourse
     * @param {number} pageId - The page ID
     * @returns {Object} Page data with pageRows containing modules
     */
    getPageLayout: builder.query({
      query: ({ pageId }) => ({
        url: pageApi(),
        method: 'POST',
        body: { method: 'findById', data: { id: pageId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (_result, _error, { pageId }) => [
        { type: 'Page', id: pageId },
        { type: 'Layout', id: pageId },
      ],
    }),

    // NOTE: There is no updateLayout API method
    // Layout is updated by:
    // 1. Creating/deleting rows with page-row/create and page-row/delete
    // 2. Moving modules with page-module/moveToNewPageRow
    // 3. Updating module positions with page-module/update (sortOrder, pageRowId)

    /**
     * Move module to a new row at specific position
     * Uses /api/page-module moveToNewPageRow method
     * @param {number} moduleId - The module ID to move
     * @param {number} pageRowPosition - Position for the new row (1-based index)
     * @param {number} pageId - The page ID (for cache invalidation)
     * @returns {Object} Updated module
     */
    moveModuleToNewRow: builder.mutation({
      query: ({ moduleId, pageRowPosition }) => ({
        url: pageModuleApi(),
        method: 'POST',
        body: { method: 'moveToNewPageRow', data: { id: moduleId, pageRowPosition } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { pageId }) => [
        { type: 'Page', id: pageId },
        { type: 'Layout', id: pageId },
        { type: 'Module', id: 'LIST' },
      ],
    }),

    /**
     * Create new row on page
     * Uses /api/page-row create method
     * @param {number} pageId - The page ID
     * @returns {Object} Created page row
     */
    createPageRow: builder.mutation({
      query: ({ pageId }) => ({
        url: pageRowApi(),
        method: 'POST',
        body: { method: 'create', data: { pageId } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { pageId }) => [
        { type: 'Page', id: pageId },
        { type: 'Layout', id: pageId },
      ],
    }),

    /**
     * Delete row from page
     * Uses /api/page-row delete method
     * @param {number} id - The row ID
     * @param {number} pageId - The page ID (for cache invalidation)
     * @returns {Object} Deletion confirmation
     */
    deletePageRow: builder.mutation({
      query: ({ id }) => ({
        url: pageRowApi(),
        method: 'POST',
        body: { method: 'delete', data: { id } },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (_result, _error, { pageId }) => [
        { type: 'Page', id: pageId },
        { type: 'Layout', id: pageId },
      ],
    }),

    // ==================== MODULE-SPECIFIC OPERATIONS ====================
    // Note: File uploads are handled by fileUploaderApi (useImageUploadMutation, useFileUploadMutation)
    // Import from: features/fileUploader/hooks/useFileUploaderActions

    /**
     * Save user-specific module data (for tasks/quizzes)
     * @param {number} moduleId - The module ID
     * @param {number} pageId - The page ID (for cache invalidation)
     * @param {Object} userdata - User-specific data to save
     * @returns {Object} Saved userdata confirmation
     */
    saveModuleUserData: builder.mutation({
      query: ({ moduleId, userdata }) => ({
        url: pageModuleApi(),
        method: 'POST',
        body: { method: 'saveUserData', data: { id: moduleId, userdata } },
      }),
      transformResponse: (response) => response?.data ?? null,
      // User-specific data doesn't invalidate the module itself, just the user's data
      invalidatesTags: (_result, _error, { moduleId, pageId }) => [
        { type: 'Module', id: moduleId },
      ],
    }),

  }),
});

// Export hooks for usage in components
export const {
  // Course structure queries
  useGetCourseForBuilderQuery,

  // Course structure mutations
  useUpdateCourseTocMutation,
  useCreateSectionMutation,
  useDeleteSectionMutation,
  useDeleteQuizSectionMutation,
  useRenameSectionMutation,
  useDuplicateSectionMutation,
  useCreatePageMutation,
  useUpdatePageMutation,
  useMovePageToSectionMutation,
  useDeletePageMutation,
  useDuplicatePageMutation,
  useReorderPagesMutation,
  useReorderSectionsMutation,

  // Page module queries
  useGetPageModulesQuery,

  // Page module mutations
  useCreatePageModuleMutation,
  useUpdatePageModuleMutation,
  useDeletePageModuleMutation,
  useDuplicatePageModuleMutation,

  // Page layout queries
  useGetPageLayoutQuery,

  // Page layout mutations (page-row API)
  useCreatePageRowMutation,
  useDeletePageRowMutation,
  useMoveModuleToNewRowMutation,

  // Module-specific operations
  useSaveModuleUserDataMutation,
} = editCourseApiSlice;

